import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useOrgId } from "../utils/tenant";
import KPIBar from "../components/dashboard/KPIBar";
import StaffActivityChart from "../components/dashboard/StaffActivityChart";
import ShiftDonutChart from "../components/dashboard/ShiftDonutChart";
import OperationalFlowChart from "../components/dashboard/OperationalFlowChart";
import StoreHealthBars from "../components/dashboard/StoreHealthBars";

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function startOfYesterday() {
  const d = startOfToday();
  d.setDate(d.getDate() - 1);
  return d;
}

const toSec = (v) =>
  v?.seconds ??
  (v?.toDate ? Math.floor(v.toDate().getTime() / 1000) : typeof v === "number" ? v : null);

const DEFAULT_ABANDONED_SHIFT_HOURS = 18;
const clampAbandonedShiftHours = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_ABANDONED_SHIFT_HOURS;
  return Math.min(48, Math.max(1, Math.round(n)));
};

function getLogDate(v) {
  return v?.toDate ? v.toDate() : null;
}

function getShiftOutSec(data) {
  const actual = toSec(data?.actualClockOut);
  const current = toSec(data?.clockOut);
  const threshold = toSec(data?.thresholdClockOut);

  if (actual != null) return actual;
  if (current != null) return current;
  if (threshold != null) return threshold;
  return null;
}

function wasThresholdAutoClocked(data) {
  if (data?.autoClockedOutAtThreshold) return true;

  const thresholdOut = toSec(data?.thresholdClockOut);
  const currentOut = toSec(data?.clockOut);

  return thresholdOut != null && currentOut != null && thresholdOut === currentOut;
}

function getDurationHours(clockInSec, outSec) {
  if (clockInSec == null || outSec == null) return null;
  let diff = outSec - clockInSec;
  if (diff < 0) diff += 24 * 3600;
  return diff / 3600;
}

function StatusChip({ tone = "neutral", text }) {
  const tones = {
    good: {
      bg: "rgba(34,197,94,0.14)",
      border: "rgba(34,197,94,0.26)",
      dot: "#22c55e",
      color: "#d9ffe6",
    },
    warn: {
      bg: "rgba(245,158,11,0.14)",
      border: "rgba(245,158,11,0.26)",
      dot: "#f59e0b",
      color: "#fff0cf",
    },
    bad: {
      bg: "rgba(239,68,68,0.14)",
      border: "rgba(239,68,68,0.26)",
      dot: "#ef4444",
      color: "#ffdede",
    },
    info: {
      bg: "rgba(71,215,244,0.14)",
      border: "rgba(71,215,244,0.26)",
      dot: "#47d7f4",
      color: "#d8f7ff",
    },
    neutral: {
      bg: "rgba(148,163,184,0.14)",
      border: "rgba(148,163,184,0.26)",
      dot: "#94a3b8",
      color: "#e2e8f0",
    },
  };

  const t = tones[tone] || tones.neutral;

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 12px",
        borderRadius: 999,
        background: t.bg,
        border: `1px solid ${t.border}`,
        color: t.color,
        fontSize: 12,
        fontWeight: 800,
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: t.dot,
          boxShadow: `0 0 12px ${t.dot}`,
          display: "inline-block",
        }}
      />
      {text}
    </div>
  );
}

function SignalCard({ title, body, tone = "neutral", action, onClick }) {
  const border =
    tone === "good"
      ? "rgba(34,197,94,0.18)"
      : tone === "warn"
        ? "rgba(245,158,11,0.18)"
        : tone === "bad"
          ? "rgba(239,68,68,0.18)"
          : "rgba(71,215,244,0.18)";

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: `1px solid ${border}`,
        background: "linear-gradient(180deg, rgba(10,24,54,0.96), rgba(5,14,33,0.96))",
        borderRadius: 18,
        padding: 14,
        textAlign: "left",
        color: "#fff",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <div style={{ fontSize: 12, opacity: 0.68 }}>{title}</div>
      <div style={{ marginTop: 8, fontSize: 14, fontWeight: 800, lineHeight: 1.4 }}>{body}</div>
      <div style={{ marginTop: 10, fontSize: 11, opacity: 0.7 }}>{action}</div>
    </button>
  );
}

export default function OverviewTab({ siteScope = { allSites: true, siteIds: [] } }) {
  const navigate = useNavigate();
  const orgId = useOrgId();

  const [metrics, setMetrics] = useState({
    activeStores: 0,
    activeStaff: 0,
    activeStaffYesterday: 0,
    submissionsToday: 0,
    submissionsYesterday: 0,
    autoClockOutsToday: 0,
    autoClockOutsYesterday: 0,
    overtimeToday: 0,
    overtimeYesterday: 0,
    openShiftsToday: 0,
    normalClosedToday: 0,
    loading: true,
  });

  useEffect(() => {
    if (!orgId) return;

    let cancelled = false;

    const run = async () => {
      const todayStart = startOfToday();
      const yesterdayStart = startOfYesterday();
      const tomorrowStart = new Date(todayStart);
      tomorrowStart.setDate(tomorrowStart.getDate() + 1);

      const todayStartMs = todayStart.getTime();
      const yesterdayStartMs = yesterdayStart.getTime();
      const tomorrowStartMs = tomorrowStart.getTime();

      const overtimeThreshold = +(localStorage.getItem("labor_threshold") || 9);
      let abandonedShiftHours = DEFAULT_ABANDONED_SHIFT_HOURS;
      try {
        const laborSnap = await getDoc(doc(db, "orgs", orgId, "settings", "labor"));
        const laborData = laborSnap.exists() ? laborSnap.data() || {} : {};
        abandonedShiftHours = clampAbandonedShiftHours(laborData.abandonedShiftHours);
      } catch {}

      let storeIds = [];
      let archivedNames = new Set();

      try {
        const [storesSnap, usersSnap] = await Promise.all([
          getDocs(collection(db, "orgs", orgId, "cashup_stores")),
          getDocs(
            query(
              collection(db, "orgs", orgId, "cashup_users"),
              where("archived", "==", true)
            )
          ),
        ]);

        storeIds = storesSnap.docs.map((d) => d.id).filter(Boolean);
        if (!siteScope.allSites) {
          const allowed = new Set(siteScope.siteIds || []);
          storeIds = storeIds.filter((id) => allowed.has(id));
        }
        archivedNames = new Set(
          usersSnap.docs
            .map((d) => d.data() || {})
            .filter((d) => d.archived)
            .map((d) => String(d.name || "").trim().toLowerCase())
            .filter(Boolean)
        );
      } catch {
        storeIds = [];
        archivedNames = new Set();
      }

      const [legacyShiftSnap, nestedShiftSnaps, nestedSubmissionSnaps] = await Promise.all([
        getDocs(query(collection(db, "cashup_shifts"), where("orgId", "==", orgId))).catch(() => ({ docs: [] })),
        storeIds.length
          ? Promise.all(
              storeIds.map((storeId) =>
                getDocs(collection(db, "orgs", orgId, "stores", storeId, "shifts"))
                  .then((snap) => ({ storeId, snap }))
                  .catch(() => ({ storeId, snap: null }))
              )
            )
          : Promise.resolve([]),
        storeIds.length
          ? Promise.all(
              storeIds.map(async (storeId) => {
                // C8.2: Overview only compares today with yesterday. Do not download
                // the store's complete submission history just to calculate those KPIs.
                // Support the timestamp fields used by legacy/current submissions and
                // de-duplicate documents that match more than one field.
                const submissionsRef = collection(db, "orgs", orgId, "stores", storeId, "submissions");
                const recentById = new Map();
                const timestampFields = ["submittedAt", "ts", "createdAt"];

                await Promise.all(
                  timestampFields.map((field) =>
                    getDocs(query(submissionsRef, where(field, ">=", yesterdayStart)))
                      .then((snap) => {
                        snap.docs.forEach((docSnap) => recentById.set(docSnap.id, docSnap));
                      })
                      .catch(() => {})
                  )
                );

                return { storeId, snap: { docs: Array.from(recentById.values()) } };
              })
            )
          : Promise.resolve([]),
      ]);

      if (cancelled) return;

      const shiftRows = [];

      (legacyShiftSnap.docs || []).forEach((docSnap) => {
        const data = docSnap.data() || {};
        if (data.orgId !== orgId) return;
        const legacyStore = data.storeId || data.store || "";
        if (!siteScope.allSites && !(siteScope.siteIds || []).includes(legacyStore)) return;
        shiftRows.push({
          store: data.store || data.storeId || "",
          data,
        });
      });

      nestedShiftSnaps.forEach(({ storeId, snap }) => {
        if (!snap) return;
        snap.docs.forEach((docSnap) => {
          shiftRows.push({
            store: storeId,
            data: docSnap.data() || {},
          });
        });
      });

      let autoClockOutsToday = 0;
      let autoClockOutsYesterday = 0;
      let overtimeToday = 0;
      let overtimeYesterday = 0;
      let normalClosedToday = 0;

      const activeMap = new Map();
      const yesterdayActiveMap = new Map();

      for (const row of shiftRows) {
        const data = row.data || {};
        const nm = String(data.name || "").trim().toLowerCase();
        if (data.archived || archivedNames.has(nm)) continue;

        const clockInSec = toSec(data.clockIn);
        if (clockInSec == null) continue;

        const clockInMs = clockInSec * 1000;
        const store = row.store || data.store || data.storeId || "";
        const outSec = getShiftOutSec(data);
        const durationHours = getDurationHours(clockInSec, outSec);
        const thresholdAuto = wasThresholdAutoClocked(data);

        const isToday = clockInMs >= todayStartMs && clockInMs < tomorrowStartMs;
        const isYesterday = clockInMs >= yesterdayStartMs && clockInMs < todayStartMs;

        // Operational truth: a shift is active until a real clock-out is stored.
        // Never make Active Staff / Dashboard disappear just because the configured
        // abandoned-shift age elapsed; forgotten clock-outs must remain visible.
        const explicitClockOut =
          toSec(data.clockOut) ??
          toSec(data.clockOutAt) ??
          toSec(data.out) ??
          toSec(data.outAt);
        const isCurrentlyActive = explicitClockOut == null;

        if (isCurrentlyActive) {
          const activeName = data.name || data.staffName || data.employeeName || "";
          const key = `${String(activeName).trim().toLowerCase()}|${String(store)}`;
          const prev = activeMap.get(key);
          if (!prev || clockInSec > (prev.clockInSec || 0)) {
            activeMap.set(key, { name: activeName, store, clockInSec });
          }
        }

        if (isYesterday) {
          const key = `${String(data.name || "").trim().toLowerCase()}|${String(store)}`;
          const prev = yesterdayActiveMap.get(key);
          if (!prev || clockInSec > (prev.clockInSec || 0)) {
            yesterdayActiveMap.set(key, { name: data.name || "", store, clockInSec });
          }
        }

        if (thresholdAuto) {
          if (isToday) autoClockOutsToday += 1;
          if (isYesterday) autoClockOutsYesterday += 1;
        }

        if (durationHours != null && durationHours > overtimeThreshold) {
          if (isToday) overtimeToday += 1;
          if (isYesterday) overtimeYesterday += 1;
        }

        if (isToday && outSec != null && !thresholdAuto && !(durationHours != null && durationHours > overtimeThreshold)) {
          normalClosedToday += 1;
        }
      }

      const activeList = Array.from(activeMap.values());
      const activeStores = new Set(activeList.map((r) => r.store).filter(Boolean)).size;
      const activeStaff = activeList.length;
      const activeStaffYesterday = yesterdayActiveMap.size;
      const openShiftsToday = activeStaff;

      let submissionsToday = 0;
      let submissionsYesterday = 0;
      nestedSubmissionSnaps.forEach(({ snap }) => {
        if (!snap) return;
        snap.docs.forEach((docSnap) => {
          const data = docSnap.data() || {};
          const submittedAt =
            getLogDate(data.submittedAt) ||
            getLogDate(data.ts) ||
            getLogDate(data.createdAt);

          if (!submittedAt) return;
          const ms = submittedAt.getTime();
          if (ms >= todayStartMs && ms < tomorrowStartMs) submissionsToday += 1;
          else if (ms >= yesterdayStartMs && ms < todayStartMs) submissionsYesterday += 1;
        });
      });

      if (!cancelled) {
        setMetrics({
          activeStores,
          activeStaff,
          activeStaffYesterday,
          submissionsToday,
          submissionsYesterday,
          autoClockOutsToday,
          autoClockOutsYesterday,
          overtimeToday,
          overtimeYesterday,
          openShiftsToday,
          normalClosedToday,
          loading: false,
        });
      }
    };

    run().catch(() => {
      if (!cancelled) {
        setMetrics((prev) => ({ ...prev, loading: false }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [orgId, siteScope.allSites, siteScope.siteIds]);

  const {
    activeStores,
    activeStaff,
    activeStaffYesterday,
    submissionsToday,
    submissionsYesterday,
    autoClockOutsToday,
    autoClockOutsYesterday,
    overtimeToday,
    overtimeYesterday,
    openShiftsToday,
    normalClosedToday,
  } = metrics;

  const totalExceptionsToday = autoClockOutsToday + overtimeToday;
  const totalExceptionsYesterday = autoClockOutsYesterday + overtimeYesterday;
  const submissionGap = submissionsToday - submissionsYesterday;
  const exceptionDelta = totalExceptionsToday - totalExceptionsYesterday;
  const storesDelta = activeStores; // no yesterday stores snapshot available in current query layer

  const kpiItems = [
    {
      title: "Stores Active",
      value: activeStores,
      delta: storesDelta,
      sparkline: [0, Math.max(activeStores - 1, 0), Math.max(activeStores - 1, 0), activeStores],
      caption: "Stores with active staff",
      onClick: () => navigate("/cashup/active-staff"),
    },
    {
      title: "Staff Active",
      value: activeStaff,
      delta: activeStaff - activeStaffYesterday,
      sparkline: [Math.max(activeStaffYesterday - 1, 0), activeStaffYesterday, Math.max(activeStaff - 1, 0), activeStaff],
      caption: "Currently clocked in",
      onClick: () => navigate("/cashup/active-staff"),
    },
    {
      title: "Submissions",
      value: submissionsToday,
      delta: submissionGap,
      sparkline: [Math.max(submissionsYesterday - 1, 0), submissionsYesterday, Math.max(submissionsToday - 1, 0), submissionsToday],
      caption: `Yesterday ${submissionsYesterday}`,
      onClick: () => navigate("/cashup/reports"),
    },
    {
      title: "Auto Clock-outs",
      value: autoClockOutsToday,
      delta: autoClockOutsToday - autoClockOutsYesterday,
      sparkline: [Math.max(autoClockOutsYesterday - 1, 0), autoClockOutsYesterday, autoClockOutsYesterday, autoClockOutsToday],
      caption: `Yesterday ${autoClockOutsYesterday}`,
      onClick: () => navigate("/cashup/labor"),
    },
    {
      title: "Overtime",
      value: overtimeToday,
      delta: overtimeToday - overtimeYesterday,
      sparkline: [Math.max(overtimeYesterday - 1, 0), overtimeYesterday, overtimeYesterday, overtimeToday],
      caption: `Yesterday ${overtimeYesterday}`,
      onClick: () => navigate("/cashup/labor"),
    },
  ];

  const activityTrendData = [
    { label: "Stores", today: activeStores, yesterday: 0 },
    { label: "Active Staff", today: activeStaff, yesterday: activeStaffYesterday },
    { label: "Submissions", today: submissionsToday, yesterday: submissionsYesterday },
    { label: "Auto Outs", today: autoClockOutsToday, yesterday: autoClockOutsYesterday },
    { label: "Overtime", today: overtimeToday, yesterday: overtimeYesterday },
  ];

  const shiftDonutData = [
    { name: "Normal Closed Shifts", value: normalClosedToday },
    { name: "Overtime", value: overtimeToday },
    { name: "Auto Clock-outs", value: autoClockOutsToday },
    { name: "Open Shifts", value: openShiftsToday },
  ];

  const shiftSummary = [
    openShiftsToday > 0
      ? `${openShiftsToday} shifts are still open right now.`
      : "No open shifts are active right now.",
    normalClosedToday > 0
      ? `${normalClosedToday} shifts closed normally today.`
      : "No normal closed shifts recorded yet today.",
    totalExceptionsToday > 0
      ? `${totalExceptionsToday} shifts fell into exception states today.`
      : "No shift exceptions detected today.",
  ];

  const operationalFlowData = [
    {
      label: "Yesterday",
      clockins: activeStaffYesterday,
      submissions: submissionsYesterday,
      exceptions: totalExceptionsYesterday,
    },
    {
      label: "Today",
      clockins: activeStaff,
      submissions: submissionsToday,
      exceptions: totalExceptionsToday,
    },
  ];

  const flowHighlights = [
    submissionGap < 0
      ? `Submissions are down by ${Math.abs(submissionGap)} compared with yesterday.`
      : submissionGap > 0
        ? `Submissions are up by ${submissionGap} compared with yesterday.`
        : "Submissions are tracking level with yesterday.",
    exceptionDelta < 0
      ? `Exceptions improved by ${Math.abs(exceptionDelta)} compared with yesterday.`
      : exceptionDelta > 0
        ? `Exceptions increased by ${exceptionDelta} compared with yesterday.`
        : "Exceptions are unchanged against yesterday.",
  ];

  const operationalHealth =
    activeStaff > 0
      ? Math.min(100, Math.max(35, 72 + activeStores * 8 - totalExceptionsToday * 10))
      : 30;
  const submissionHealth = Math.max(20, Math.min(100, 50 + submissionGap * 12));
  const exceptionHealth = Math.max(0, Math.min(100, 100 - totalExceptionsToday * 18));

  const storeHealthData = [
    {
      name: "Operational Health",
      value: operationalHealth,
      meta: `${activeStores} active stores • ${activeStaff} staff`,
    },
    {
      name: "Submission Health",
      value: submissionHealth,
      meta: `Today ${submissionsToday} • Yesterday ${submissionsYesterday}`,
    },
    {
      name: "Exception Health",
      value: exceptionHealth,
      meta: `Exceptions today ${totalExceptionsToday}`,
    },
  ];

  const insights = [
    totalExceptionsToday > 0
      ? `${totalExceptionsToday} shift exceptions need attention today.`
      : "No shift exceptions detected so far today.",
    submissionsToday < submissionsYesterday
      ? `Submission pace is slower than yesterday by ${submissionsYesterday - submissionsToday}.`
      : submissionsToday > submissionsYesterday
        ? `Submission pace is ahead of yesterday by ${submissionsToday - submissionsYesterday}.`
        : "Submission pace is matching yesterday.",
    openShiftsToday > 0
      ? `${openShiftsToday} staff are still on active open shifts right now.`
      : "No open shifts are currently active.",
  ];

  const headerTone = totalExceptionsToday > 0 ? "warn" : "good";
  const headerText =
    totalExceptionsToday > 0
      ? `${totalExceptionsToday} exceptions need review`
      : "Operations stable right now";

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 31, fontWeight: 900, lineHeight: 1.05 }}>Overview</div>
          <div style={{ marginTop: 5, fontSize: 13, opacity: 0.72 }}>
            Live operations, exceptions and shift movement in one view.
          </div>
        </div>
        <StatusChip tone={headerTone} text={headerText} />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <SignalCard
          title="Focus now"
          body={openShiftsToday > 0 ? `${openShiftsToday} active shifts are still open.` : "No open shifts need attention right now."}
          tone="info"
          action="Open Active Staff"
          onClick={() => navigate("/cashup/active-staff")}
        />
        <SignalCard
          title="Submission pace"
          body={
            submissionGap < 0
              ? `Running ${Math.abs(submissionGap)} behind yesterday.`
              : submissionGap > 0
                ? `Running ${submissionGap} ahead of yesterday.`
                : "Tracking level with yesterday."
          }
          tone={submissionGap < 0 ? "warn" : "good"}
          action="Open Reports"
          onClick={() => navigate("/cashup/reports")}
        />
        <SignalCard
          title="Exceptions"
          body={
            totalExceptionsToday > 0
              ? `${totalExceptionsToday} shift exceptions recorded today.`
              : "No shift exceptions recorded today."
          }
          tone={totalExceptionsToday > 0 ? "bad" : "good"}
          action="Open Labor"
          onClick={() => navigate("/cashup/labor")}
        />
      </div>

      <KPIBar items={kpiItems} />

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,2fr) minmax(320px,1fr)", gap: 14, marginTop: 16 }}>
        <StaffActivityChart
          data={activityTrendData}
          title="Live Operations Snapshot"
          subtitle="Today vs yesterday across your key operational signals."
        />
        <ShiftDonutChart
          data={shiftDonutData}
          summary={shiftSummary}
          title="Shift Outcomes"
          subtitle="Closed shifts and still-open shifts are separated clearly."
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,2fr) minmax(320px,1fr)", gap: 14, marginTop: 14 }}>
        <OperationalFlowChart
          data={operationalFlowData}
          highlights={flowHighlights}
          title="Operational Flow"
          subtitle="Clock-ins, submissions and exceptions compared across today and yesterday."
        />
        <StoreHealthBars
          data={storeHealthData}
          insights={insights}
          title="Operations Health"
          subtitle="A visual health check with quick intelligence."
        />
      </div>
    </div>
  );
}
